import type { JSX } from 'react';

import { Link } from 'Components/Buttons/Buttons';
import { CollapsibleModal } from 'Components/Modal/CollapsibleModal/CollapsibleModal';
import { Text } from 'Components/Text/Text';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { getExceptionDetailsUrl } from 'Helpers/URLBuilder/URLBuilder';
import { DashboardType, IssuesExceptionsDashboardType } from 'Models/Dashboards';
import { ExceptionResponse } from 'Models/Exceptions';
import { OrganizationResponseBase } from 'Models/Organization';

import styles from './HistoricalExceptionsDisplayModal.module.css';

export enum ClosedExceptionModalDisplayType {
    OPEN,
    CLOSED,
}

export interface HistoricalExceptionsDisplayModalProps {
    hideModal: () => void;
    selectedException: ExceptionResponse[];
    date: string;
    modalType: ClosedExceptionModalDisplayType;
    dashboardType: IssuesExceptionsDashboardType;
    organizations?: OrganizationResponseBase[];
}

// TODO: rename this. It's not just for closed exceptions.
export const HistoricalExceptionsDisplayModal = (props: HistoricalExceptionsDisplayModalProps): JSX.Element => {
    const organizationIdToNameMap: Map<string, string> = new Map(props.organizations?.map((organization) => [organization.id, organization.name]));

    const displayExceptionDate = (exception: ExceptionResponse) => {
        switch (props.modalType) {
            case ClosedExceptionModalDisplayType.CLOSED:
                return iso8601ToUsDateShort(exception.closed_timestamp!);
            case ClosedExceptionModalDisplayType.OPEN:
                return iso8601ToUsDateShort(exception.created_timestamp);
        }
    };

    const displayHeaderText = () => {
        switch (props.modalType) {
            case ClosedExceptionModalDisplayType.CLOSED:
                return 'Closed Exceptions';
            case ClosedExceptionModalDisplayType.OPEN:
                return 'Open Exceptions';
        }
    };

    const sortExceptionsByDateType = (exceptionA: ExceptionResponse, exceptionB: ExceptionResponse): number => {
        if (props.modalType === ClosedExceptionModalDisplayType.CLOSED) {
            if (exceptionA.closed_timestamp! > exceptionB.closed_timestamp!) {
                return 1;
            } else if (exceptionA.closed_timestamp! < exceptionB.closed_timestamp!) {
                return -1;
            } else {
                return exceptionA.title.localeCompare(exceptionB.title, undefined, { numeric: true, sensitivity: 'base' });
            }
        } else {
            if (exceptionA.created_timestamp > exceptionB.created_timestamp) {
                return 1;
            } else if (exceptionA.created_timestamp < exceptionB.created_timestamp) {
                return -1;
            } else {
                return exceptionA.title.localeCompare(exceptionB.title, undefined, { numeric: true, sensitivity: 'base' });
            }
        }
    };

    const exceptionListing = props.selectedException.sort(sortExceptionsByDateType).map((exception) => (
        <div className={styles.detailsContainer} key={exception.created_timestamp}>
            {props.dashboardType !== DashboardType.CONTROL_AUTOMATION && organizationIdToNameMap.size > 0 && (
                <div className={styles.detailsOrganizationName}>
                    <Text color="darkGray" noStyles={true}>
                        {organizationIdToNameMap.get(exception.impacted_vendor!)}
                    </Text>
                </div>
            )}
            <Link to={getExceptionDetailsUrl(exception.id, exception.type)} size="sm" openInNewTab>
                {exception.title}
            </Link>
            <div className={styles.detailsDateContainer}>
                <Text color="darkGray" noStyles={true}>
                    {displayExceptionDate(exception)}
                </Text>
            </div>
        </div>
    ));

    return <CollapsibleModal headerText={{ text: displayHeaderText(), secondaryText: `${props.date}` }} hideModal={props.hideModal} displayItems={exceptionListing} />;
};
